/**
 * Configuration API client — loan products, payees (identity/contact only),
 * business entities (the parent grouping that owns payees), and the org's
 * singleton base rate config (the six commission rates the run screen
 * pre-fills from).
 *
 * Org-scoped like the rest of the app: the caller supplies the org UUID from
 * auth state. All resources are addressed by UUID. Mirrors the shape and
 * conventions of commissionReportsApi.js.
 */
import api from './client';

const base = (orgUuid) => `/organisations/${orgUuid}`;

export const configApi = {
    // ── Products ─────────────────────────────────────────────────────────

    listProducts(orgUuid) {
        return api.get(`${base(orgUuid)}/products`).then((r) => r.data);
    },

    getProduct(orgUuid, productUuid) {
        return api.get(`${base(orgUuid)}/products/${productUuid}`).then((r) => r.data);
    },

    /**
     * @param product { funderUuid, name, code, funderUpfrontRate, funderTrailRate }
     *   funderUuid is required — every product belongs to one funder.
     *   code is optional. rates are decimal fractions (1% = 0.01)
     */
    createProduct(orgUuid, product) {
        return api.post(`${base(orgUuid)}/products`, product).then((r) => r.data);
    },

    updateProduct(orgUuid, productUuid, product) {
        return api.put(`${base(orgUuid)}/products/${productUuid}`, product).then((r) => r.data);
    },

    deleteProduct(orgUuid, productUuid) {
        return api.delete(`${base(orgUuid)}/products/${productUuid}`).then((r) => r.data);
    },

    // ── Funders (product grouping) ───────────────────────────────────────

    listFunders(orgUuid) {
        return api.get(`${base(orgUuid)}/funders`).then((r) => r.data);
    },

    getFunder(orgUuid, funderUuid) {
        return api.get(`${base(orgUuid)}/funders/${funderUuid}`).then((r) => r.data);
    },

    /** @param funder { name } */
    createFunder(orgUuid, funder) {
        return api.post(`${base(orgUuid)}/funders`, funder).then((r) => r.data);
    },

    updateFunder(orgUuid, funderUuid, funder) {
        return api.put(`${base(orgUuid)}/funders/${funderUuid}`, funder).then((r) => r.data);
    },

    deleteFunder(orgUuid, funderUuid) {
        return api.delete(`${base(orgUuid)}/funders/${funderUuid}`).then((r) => r.data);
    },

    /** Products registered under one funder. */
    listProductsByFunder(orgUuid, funderUuid) {
        return api
            .get(`${base(orgUuid)}/funders/${funderUuid}/products`)
            .then((r) => r.data);
    },

    // ── Business entities (parent grouping that owns payees) ─────────────

    /**
     * List the org's business entities. Each entity may include its payees
     * inline (entity.payees: Payee[]) so the master/detail view can render
     * without a second round-trip; if the backend omits them, the UI falls
     * back to listPayeesByEntity().
     */
    listBusinessEntities(orgUuid) {
        return api.get(`${base(orgUuid)}/business-entities`).then((r) => r.data);
    },

    getBusinessEntity(orgUuid, entityUuid) {
        return api
            .get(`${base(orgUuid)}/business-entities/${entityUuid}`)
            .then((r) => r.data);
    },

    /**
     * @param entity {
     *   name, abn,
     *   contactName, contactEmail, contactPhone, address
     * }
     */
    createBusinessEntity(orgUuid, entity) {
        return api
            .post(`${base(orgUuid)}/business-entities`, entity)
            .then((r) => r.data);
    },

    updateBusinessEntity(orgUuid, entityUuid, entity) {
        return api
            .put(`${base(orgUuid)}/business-entities/${entityUuid}`, entity)
            .then((r) => r.data);
    },

    deleteBusinessEntity(orgUuid, entityUuid) {
        return api
            .delete(`${base(orgUuid)}/business-entities/${entityUuid}`)
            .then((r) => r.data);
    },

    /** Payees belonging to one business entity. */
    listPayeesByEntity(orgUuid, entityUuid) {
        return api
            .get(`${base(orgUuid)}/business-entities/${entityUuid}/payees`)
            .then((r) => r.data);
    },

    // ── Payees (identity / contact only; belong to a business entity) ────

    /**
     * @param type optional 'SUBCONTRACTOR' | 'REFERRER' to filter
     * @param businessEntityUuid optional filter to one entity
     */
    listPayees(orgUuid, { type, businessEntityUuid } = {}) {
        const params = {};
        if (type) params.type = type;
        if (businessEntityUuid) params.businessEntityUuid = businessEntityUuid;
        return api
            .get(`${base(orgUuid)}/payees`, { params })
            .then((r) => r.data);
    },

    getPayee(orgUuid, payeeUuid) {
        return api.get(`${base(orgUuid)}/payees/${payeeUuid}`).then((r) => r.data);
    },

    /**
     * @param payee {
     *   businessEntityUuid, type, name, code,
     *   contactName, contactEmail, contactPhone
     * }
     *   businessEntityUuid is required — every payee belongs to one entity.
     */
    createPayee(orgUuid, payee) {
        return api.post(`${base(orgUuid)}/payees`, payee).then((r) => r.data);
    },

    updatePayee(orgUuid, payeeUuid, payee) {
        return api.put(`${base(orgUuid)}/payees/${payeeUuid}`, payee).then((r) => r.data);
    },

    deletePayee(orgUuid, payeeUuid) {
        return api.delete(`${base(orgUuid)}/payees/${payeeUuid}`).then((r) => r.data);
    },

    // ── Rate config (singleton per org) ──────────────────────────────────

    /**
     * The org's saved base rates. Returns an object whose fields are null
     * when nothing has been saved yet, so the UI can render a blank form.
     */
    getRateConfig(orgUuid) {
        return api.get(`${base(orgUuid)}/rate-config`).then((r) => r.data);
    },

    /**
     * Create or update the org's singleton rate config.
     * @param config {
     *   funderUpfrontRate, funderTrailRate,
     *   subbyUpfrontRate, subbyTrailRate,
     *   referrerUpfrontRate, referrerTrailRate
     * } decimal fractions (1% = 0.01)
     */
    saveRateConfig(orgUuid, config) {
        return api.put(`${base(orgUuid)}/rate-config`, config).then((r) => r.data);
    },
};